import { useState, useEffect, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-toastify';
import { 
  FaPlus, 
  FaSearch, 
  FaTimes,
  FaTag,
  FaCheck
} from 'react-icons/fa';

import { createProject, createProjectSite, createProjectPhase } from '../api/projects';
import { autocompleteAddress, getPlaceDetails, GoogleAddressSchema } from '../api/address';
import { createSafeCloseHandler } from '../utils/modalUtils';
import './CompactProjectModal.css';

// Maximum allowed length for a project name
const PROJECT_NAME_MAX_LENGTH = 255;

interface CompactProjectModalProps {
  onClose: () => void;
  onProjectCreated: (projectId: string) => Promise<void>;
}

interface ProjectFormData {
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  siteName: string;
  address: GoogleAddressSchema | null;
  phases: ProjectPhaseInterface[];
}

interface AddressSuggestion {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

export const CompactProjectModal: React.FC<CompactProjectModalProps> = ({
  onClose,
  onProjectCreated
}) => {
  const { keycloak } = useKeycloak();
  const [isLoading, setIsLoading] = useState(false);
  
  // Calculate end date 60 days from today
  const getDefaultEndDate = (startDate: string) => {
    const start = new Date(startDate);
    const end = new Date(start);
    end.setDate(start.getDate() + 60);
    return end.toISOString().split('T')[0];
  };

  const today = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    description: '',
    start_date: today,
    end_date: getDefaultEndDate(today),
    siteName: 'Main Site',
    address: null,
    phases: [
      {
        id: crypto.randomUUID(),
        project_id: '',
        name: 'Planning',
        status: 'NOT_STARTED'
      }
    ]
  });

  // Address autocomplete state
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Phase management state
  const [showPhaseForm, setShowPhaseForm] = useState(false);
  const [newPhaseName, setNewPhaseName] = useState('');

  // Create safe close handler
  const handleSafeClose = createSafeCloseHandler(
    formData, 
    onClose, 
    ['start_date', 'end_date', 'phases']
  );

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'start_date') {
      const newEndDate = getDefaultEndDate(value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        end_date: newEndDate
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle phase creation
  const handleCreatePhase = () => {
    if (!newPhaseName.trim()) return;

    const newPhase: ProjectPhaseInterface = {
      id: crypto.randomUUID(),
      project_id: '',
      name: newPhaseName.trim(),
      status: 'NOT_STARTED'
    };

    setFormData(prev => ({
      ...prev,
      phases: [...prev.phases, newPhase]
    }));

    setNewPhaseName('');
    setShowPhaseForm(false);
    toast.success('Phase added successfully');
  };

  // Handle phase removal
  const handleRemovePhase = (phaseId: string) => {
    setFormData(prev => ({
      ...prev,
      phases: prev.phases.filter(phase => phase.id !== phaseId)
    }));
  };

  // Update dropdown position when showing suggestions
  const updateDropdownPosition = () => {
    if (addressInputRef.current) {
      const rect = addressInputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  };

  // Address autocomplete with debouncing
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      // Don't search if we already have a selected address and the query matches it
      if (formData.address && addressQuery === formData.address.formatted_address) {
        setAddressSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      if (addressQuery.length >= 3 && keycloak.token) {
        setIsLoadingAddress(true);
        try {
          const result = await autocompleteAddress(keycloak.token, addressQuery);
          setAddressSuggestions(result.predictions || []);
          updateDropdownPosition(); // Update position before showing
          setShowSuggestions(true);
        } catch (error) {
          console.error('Error fetching address suggestions:', error);
          setAddressSuggestions([]);
        } finally {
          setIsLoadingAddress(false);
        }
      } else {
        setAddressSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [addressQuery, keycloak.token, formData.address]);

  // Handle address selection
  const handleAddressSelect = async (suggestion: AddressSuggestion) => {
    if (!keycloak.token) return;

    setIsLoadingAddress(true);
    try {
      const placeDetails = await getPlaceDetails(keycloak.token, suggestion.place_id);
      setFormData(prev => ({
        ...prev,
        address: placeDetails
      }));
      setAddressQuery(placeDetails.formatted_address);
      setShowSuggestions(false);
    } catch (error) {
      console.error('Error fetching place details:', error);
      toast.error('Failed to load address details');
    } finally {
      setIsLoadingAddress(false);
    }
  };

  // Clear selected address
  const clearAddress = () => {
    setFormData(prev => ({ ...prev, address: null }));
    setAddressQuery('');
    setShowSuggestions(false);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!keycloak.token) {
      toast.error('Authentication required');
      return;
    }

    // Validate required fields
    if (!formData.name.trim()) {
      toast.error('Project name is required');
      return;
    }

    if (!formData.siteName.trim()) {
      toast.error('Site name is required');
      return;
    }

    if (!formData.address) {
      toast.error('Address is required');
      return;
    }

    if (!formData.start_date) {
      toast.error('Start date is required');
      return;
    }

    if (!formData.end_date) {
      toast.error('End date is required');
      return;
    }

    setIsLoading(true);
    try {
      // Step 1: Create project
      const projectData: ProjectInterface = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
        owner_id: keycloak.tokenParsed?.sub || '',
        status: 'NOT_STARTED',
        id: crypto.randomUUID()
      };

      const newProject = await createProject(keycloak.token, projectData);
      const projectId = newProject.id || projectData.id || '';

      // Step 2: Create phases for the project
      if (formData.phases.length > 0 && projectId && keycloak.token) {
        await Promise.all(
          formData.phases.map(async (phase) => {
            const phaseData = {
              ...phase,
              project_id: projectId
            };
            return createProjectPhase(keycloak.token!, projectId, phaseData);
          })
        );
      }

      // Step 3: Create site with Google address data if address is selected
      if (formData.address && projectId) {
        const siteData: ProjectSiteItemInterface = {
          id: crypto.randomUUID(),
          name: formData.siteName,
          description: `Site for ${formData.name}`,
          address: formData.address.formatted_address,
          geo_location: [formData.address.latitude, formData.address.longitude] as [number, number],
          project_id: projectId,
          google_address_data: {
            place_id: formData.address.place_id,
            formatted_address: formData.address.formatted_address,
            street_number: formData.address.street_number,
            route: formData.address.route,
            city: formData.address.city,
            state: formData.address.state,
            locality: formData.address.locality,
            administrative_area_level_1: formData.address.administrative_area_level_1,
            country: formData.address.country,
            postal_code: formData.address.postal_code,
            latitude: formData.address.latitude,
            longitude: formData.address.longitude,
            timezone: formData.address.timezone
          }
        };

        await createProjectSite(keycloak.token, projectId, siteData);
      }
      
      toast.success('Project created successfully!');
      await onProjectCreated(projectId);
      onClose();
    } catch (error: any) {
      console.error('Error creating project:', error);
      toast.error(error.message || 'Failed to create project');
    } finally {
      setIsLoading(false);
    }
  };

  // Close suggestions when clicking outside and handle scroll updates
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target as Node) &&
        addressInputRef.current &&
        !addressInputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    const handleScroll = () => {
      if (showSuggestions) {
        updateDropdownPosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true); // true for capture phase
    window.addEventListener('resize', handleScroll);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [showSuggestions]);

  return (
    <div className="compact-modal-overlay" onClick={handleSafeClose}>
      <button
        onClick={() => handleSafeClose()}
        className="compact-modal-close"
      >
        <FaTimes />
      </button>
      
      <div className="compact-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="compact-modal-header">
          <h2 className="compact-modal-title">Create New Project</h2>
        </div>

        <form onSubmit={handleSubmit} className="compact-modal-form">
          {/* Project Name */}
          <div className="compact-modal-field">
            <div className="compact-modal-label-row">
              <label className="compact-modal-label">Project Name *</label>
              <span
                className={`compact-modal-counter${
                  formData.name.length >= PROJECT_NAME_MAX_LENGTH
                    ? ' compact-modal-counter--limit'
                    : formData.name.length >= PROJECT_NAME_MAX_LENGTH * 0.9
                    ? ' compact-modal-counter--warn'
                    : ''
                }`}
              >
                {formData.name.length}/{PROJECT_NAME_MAX_LENGTH}
              </span>
            </div>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter project name"
              maxLength={PROJECT_NAME_MAX_LENGTH}
              required
              className="compact-modal-input"
              disabled={isLoading}
            />
          </div>

          {/* Scope of Work */}
          <div className="compact-modal-field">
            <label className="compact-modal-label">Scope of work</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe the project objectives and scope of work..."
              rows={2}
              className="compact-modal-textarea"
              disabled={isLoading}
            />
          </div>

          {/* Site Name */}
          <div className="compact-modal-field">
            <label className="compact-modal-label">Site Name *</label>
            <input
              type="text"
              name="siteName"
              value={formData.siteName}
              onChange={handleInputChange}
              placeholder="Enter site name"
              required
              className="compact-modal-input"
              disabled={isLoading}
            />
          </div>

          {/* Project Phases */}
          <div className="compact-modal-field">
            <label className="compact-modal-label">
              Project Phases <span className="compact-modal-hint">("Planning" added by default)</span>
            </label>
            
            {/* Existing Phases */}
            {formData.phases.length > 0 && (
              <div className="compact-modal-phases">
                {formData.phases.map((phase) => (
                  <div key={phase.id} className="compact-modal-phase-item">
                    <div className="compact-modal-phase-content">
                      <FaTag className="compact-modal-phase-icon" />
                      <span className="compact-modal-phase-name">{phase.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemovePhase(phase.id)}
                      className="compact-modal-phase-remove"
                      disabled={isLoading}
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Phase */}
            {!showPhaseForm ? (
              <button
                type="button"
                onClick={() => setShowPhaseForm(true)}
                className="compact-modal-add-phase"
                disabled={isLoading}
              >
                <FaPlus />
                <span>Add Phase</span>
              </button>
            ) : (
              <div className="compact-modal-phase-form">
                <input
                  type="text"
                  value={newPhaseName}
                  onChange={(e) => setNewPhaseName(e.target.value)}
                  placeholder="Enter phase name..."
                  className="compact-modal-phase-input"
                  autoFocus
                  disabled={isLoading}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleCreatePhase();
                    } else if (e.key === 'Escape') {
                      setShowPhaseForm(false);
                      setNewPhaseName('');
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleCreatePhase}
                  disabled={!newPhaseName.trim() || isLoading}
                  className="compact-modal-phase-confirm"
                >
                  <FaCheck />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPhaseForm(false);
                    setNewPhaseName('');
                  }}
                  className="compact-modal-phase-cancel"
                  disabled={isLoading}
                >
                  <FaTimes />
                </button>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="compact-modal-dates">
            <div className="compact-modal-field">
              <label className="compact-modal-label">Start Date *</label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                required
                className="compact-modal-input"
                disabled={isLoading}
              />
            </div>
            <div className="compact-modal-field">
              <label className="compact-modal-label">
                End Date * <span className="compact-modal-hint">(+60 days)</span>
              </label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
                min={formData.start_date}
                required
                className="compact-modal-input"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Address Search */}
          <div className="compact-modal-field">
            <label className="compact-modal-label">Address *</label>
            <div className="compact-modal-address-container">
              <div className="compact-modal-address-icon">
                {isLoadingAddress ? (
                  <FaSearch className="loading" />
                ) : (
                  <FaSearch />
                )}
              </div>
              <input
                ref={addressInputRef}
                type="text"
                value={addressQuery}
                onChange={(e) => setAddressQuery(e.target.value)}
                placeholder="Search for project address..."
                className="compact-modal-address-input"
                disabled={isLoading}
              />
              {addressQuery && (
                <button
                  type="button"
                  onClick={clearAddress}
                  className="compact-modal-address-clear"
                  disabled={isLoading}
                >
                  <FaTimes />
                </button>
              )}

              {/* Address Suggestions Dropdown */}
              {showSuggestions && addressSuggestions.length > 0 && (
                <div 
                  ref={suggestionsRef} 
                  className="compact-modal-address-dropdown"
                  style={{
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    width: dropdownPosition.width
                  }}
                >
                  {addressSuggestions.map((suggestion) => (
                    <button
                      key={suggestion.place_id}
                      type="button"
                      onClick={() => handleAddressSelect(suggestion)}
                      className="compact-modal-address-item"
                      disabled={isLoadingAddress}
                    >
                      <div className="compact-modal-address-main">
                        {suggestion.structured_formatting.main_text}
                      </div>
                      <div className="compact-modal-address-secondary">
                        {suggestion.structured_formatting.secondary_text}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Address Display */}
            {formData.address && (
              <div className="compact-modal-selected-address">
                <div className="compact-modal-selected-content">
                  <div className="compact-modal-selected-icon">
                    <FaTag />
                  </div>
                  <div className="compact-modal-selected-info">
                    <div className="compact-modal-selected-title">Selected Location</div>
                    <div className="compact-modal-selected-address-text">
                      {formData.address.formatted_address}
                    </div>
                    <div className="compact-modal-selected-coords">
                      📍 {formData.address.latitude.toFixed(4)}, {formData.address.longitude.toFixed(4)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={clearAddress}
                    className="compact-modal-selected-remove"
                    disabled={isLoading}
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="compact-modal-buttons">
            <button
              type="button"
              onClick={onClose}
              className="compact-modal-cancel"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.name.trim() || !formData.siteName.trim() || !formData.address || !formData.start_date || !formData.end_date}
              className={`compact-modal-submit ${isLoading || !formData.name.trim() || !formData.siteName.trim() || !formData.address || !formData.start_date || !formData.end_date ? 'disabled' : ''}`}
            >
              {isLoading ? (
                <>
                  <FaPlus className="loading" />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <FaPlus />
                  <span>Create Project</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
